// keepr's public site (KPE-008): the pages a visitor with no account reads —
// the landing page at `/` (KPR-316), and later Contact (KPR-317), Terms and
// Privacy (KPR-318) and Status (KPR-320) — and the one footer they share.
//
// THE FOOTER LINKS ONLY WHAT EXISTS. Its order is fixed here, once; an entry
// shows when its page is published and not before, the rule the legal footer
// already kept (components/legal/LegalLinks.js): a link to a page that is not
// there yet — a 404, or a page that only says "coming soon" — is a promise
// the footer cannot keep. So:
//
//   - Contact and Status carry their own switch below. The item that builds
//     the page adds its route to the public site group in src/index.js and
//     sets `published: true` in the same change;
//   - Terms, Privacy and Consumer health data follow src/config/legal.js:
//     they appear once LEGAL_PUBLISHED is on and the page's reviewed text is
//     there (KPR-318 adds Terms and Privacy to LEGAL_PAGES);
//   - Docs is live.
//
// src/components/public/__tests__/PublicFooter.test.js holds every published
// link to a real route, and the order to this list.

import { BRAND_NAME, TAGLINE } from './brand';
import { footerLegalLinks } from './legal';

/** The site's own address: the canonical URL and the absolute og:image. */
export const SITE_ORIGIN = 'https://keepr.cloud';

/**
 * The footer, in the order a visitor reads it. A `legal` entry's key is its
 * page's key in src/config/legal.js LEGAL_PAGES, which decides whether it is
 * published (and the footer test holds the two paths equal).
 */
export const FOOTER_LINKS = Object.freeze([
	Object.freeze({ key: 'contact', label: 'Contact', path: '/contact', published: false }), // KPR-317
	Object.freeze({ key: 'terms', label: 'Terms', path: '/terms', legal: true }), // KPR-318
	Object.freeze({ key: 'privacy', label: 'Privacy', path: '/privacy', legal: true }), // KPR-318
	Object.freeze({ key: 'consumer-health-data', label: 'Consumer health data', path: '/consumer-health-data', legal: true }), // KPR-478
	Object.freeze({ key: 'status', label: 'Status', path: '/status', published: true }), // KPR-320
	Object.freeze({ key: 'docs', label: 'Docs', path: '/docs', published: true }),
]);

/** Whether a footer entry's page is there to link: its own switch, or for a legal page, src/config/legal.js. */
export function isPublished(link) {
	if (!link.legal) return link.published === true;
	// legal.js's own rule, the one LegalLinks follows on the sign-in pages.
	return footerLegalLinks().some(page => page.key === link.key);
}

/**
 * What the footer links: the published pages, in footer order. `published`
 * is for the tests, to see the whole footer as it will be.
 */
export function footerLinks(published = isPublished) {
	return FOOTER_LINKS.filter(link => published(link)).map(({ key, label, path }) => ({ key, label, path }));
}

/**
 * The landing page's words for whoever reads the HTML without running it:
 * the tab, search results and a chat app's link preview
 * (scripts/prerender-public.mjs writes them into build/index.html). The page
 * itself sets the same tab title once it runs (pages/Landing.js).
 */
export const LANDING_META = Object.freeze({
	path: '/',
	// useDocumentTitle adds the brand: "Keep everything. Find anything. · keepr".
	title: TAGLINE,
	description: 'Keep any record you care about, in the shape you choose. Describe it once, and keepr gives you a form to fill, a list to search and a history to trust.',
	image: '/landing/og-image.png',
	imageWidth: 1200,
	imageHeight: 630,
	imageAlt: `The ${BRAND_NAME} wordmark and its tagline beside a household inventory's items in ${BRAND_NAME}`,
});
